import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import {
  Button,
  Col,
  Form,
  Image,
  Modal,
  Row,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import PecaAiDictionarySelect, {
  PecaAiProductOption,
} from '../PecaAiDictionarySelect';
import { usePecaAiContext } from '../../PecaAiProvider';
import { useCallback, useState } from 'react';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { cepMask } from '@/utils/mask/commonMasks';
import { applyMask } from '@/utils/mask';
import { useWorkorderComplementSyncWithPecaAi } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { Check } from 'react-feather';
import formatterPrice from '@/utils/number/formatterPrice';
import { decimalToTime } from '@/utils/functions/decimalToTime';
import moment from 'moment';
import {
  useWorkOrderPecaAiAddProductInBasket,
  useWorkOrderPecaAiSearchProduct,
  WORKORDER_PECA_AI_BASKET_DETAIL,
} from '@/core/domain/workorder/pecaAi/workorder-pecaAi.hooks';
import { WorkOrderPecaAiSearchProductPossibilities } from '@/core/domain/workorder/pecaAi/workorder-pecaAi.types';
import PecaAiShippingAmountFlag from '../../PecaAiShippingAmountFlag';

interface FormValues {
  amount: number;
  description: string;
}

export default function PecaAiAddProductForm() {
  const [form] = Form.useForm<FormValues>();

  const amount = Form.useWatch('amount', form);

  const { basketId, cnpj } = usePecaAiContext();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { data: company },
  } = useWorkorderDetailContext();
  const { addProduct, postalCode, licensePlate } = usePecaAiContext();

  const searchProduct = useWorkOrderPecaAiSearchProduct();

  const [possibilities, setPossibilities] = useState<
    WorkOrderPecaAiSearchProductPossibilities[]
  >([]);

  const addProductInBasket = useWorkOrderPecaAiAddProductInBasket();
  const syncWithPecaAi = useWorkorderComplementSyncWithPecaAi();

  const queryChanel = useQueryChanel();

  const isLoading =
    searchProduct.isLoading ||
    addProductInBasket.isLoading ||
    syncWithPecaAi.isLoading;

  const onProductSelect = useCallback(
    async (option: PecaAiProductOption) => {
      if (option?.label) {
        const result = await searchProduct.mutateAsync({
          plate: licensePlate as string,
          postalCode: postalCode as string,
          products: [{ amount, description: option.label }],
        });

        if (result?.products?.length > 0) {
          const product = result.products[0];
          const resultPossibilities = product?.possibilities || [];

          if (resultPossibilities.length > 1) {
            setPossibilities(
              resultPossibilities.map((possibility) => ({
                ...possibility,
                description: (
                  possibility?.description || product.description
                ).toLocaleUpperCase(),
              })),
            );
          } else if (resultPossibilities.length === 1) {
            const possibility = resultPossibilities[0];
            handleAddProduct({
              ...possibility,
              description: (
                possibility?.description || product.description
              ).toLocaleUpperCase(),
            });
          }

          if (resultPossibilities.length) {
            return form.resetFields();
          }
        }

        Modal.confirm({
          title: 'Produto não encontrado',
          content: `Não encontramos o produto "${
            option.label
          }", para o veículo "${licensePlate}", na região ${
            company?.companyAddress.district
          } - ${applyMask(company?.companyAddress.zip, cepMask)}`,
          okButtonProps: { style: { display: 'none' } },
          cancelText: 'Fechar',
        });
      }
    },
    [amount, cnpj, basketId, workorder, complement],
  );

  const handleAddProduct = async (
    possibility: WorkOrderPecaAiSearchProductPossibilities,
  ) => {
    if (basketId && cnpj && workorder?.id && complement?.id) {
      await addProductInBasket.mutateAsync({
        amount: possibility.amount,
        productId: possibility.id,
        basketId,
        document: cnpj,
      });
      await syncWithPecaAi.mutateAsync({
        workOrderId: workorder.id,
        complementId: complement.id,
      });
      queryChanel({
        invalidateQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [WORKORDER_PECA_AI_BASKET_DETAIL],
        ],
      });
    } else {
      addProduct({
        amount: possibility.amount,
        description: possibility.description,
        brand: possibility.brand,
        inBasket: false,
        partNumber: possibility.partNumber,
        productId: possibility.id,
        totalPrice: possibility.productTotalPrice,
        unitPrice: possibility.productUnitPrice,
        deadlineHours: possibility.deadlineHours,
        image: possibility.image,
        observation:
          possibility.observation.length >= 1
            ? possibility.observation[0].description
            : undefined,
      });
    }
  };

  return (
    <>
      <Form form={form} initialValues={{ amount: 1, description: null }}>
        <strong style={{ fontSize: '1.1rem' }}>Adicionar produto</strong>
        <Row gutter={[16, 16]}>
          <Col>
            <FloatingFormItem name="amount" label="Quantidade">
              <InputDecimal min={0} disabled={isLoading} />
            </FloatingFormItem>
          </Col>

          <Col flex={1}>
            <FloatingFormItem name="description" label="Descrição do produto">
              <PecaAiDictionarySelect
                value={null}
                onProductSelect={onProductSelect}
                disabled={isLoading}
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <Modal
        open={!!possibilities.length}
        onCancel={() => setPossibilities([])}
        title="Selecione uma das variações do produto"
        width={900}
        footer={null}
      >
        <Table
          dataSource={possibilities}
          scroll={{ x: true }}
          columns={[
            {
              title: 'Produto',
              render: (_, item) => (
                <>
                  <Group gap={10}>
                    <Image
                      src={item.image}
                      preview={false}
                      width={40}
                      height={40}
                      style={{ flexShrink: 0 }}
                    />
                    <strong>{item.description}</strong>
                  </Group>

                  {!!item.observation.length && (
                    <p style={{ color: 'orange', marginTop: 16 }}>
                      {item.observation[0].description}
                    </p>
                  )}
                </>
              ),
            },
            { title: 'Cód. peça', dataIndex: 'partNumber' },
            { title: 'Marca', dataIndex: 'brand' },
            {
              title: 'Frete',
              render: (_, item) => (
                <PecaAiShippingAmountFlag value={item.shippingPrice} />
              ),
            },
            {
              title: 'Preço',
              render: (_, item) => formatterPrice(item.productUnitPrice),
            },
            {
              title: 'Prev. de entrega',
              align: 'center',
              width: 160,
              render: (_, item) => {
                if (!item.expectedDelivery) {
                  return decimalToTime(item.deadlineHours);
                }

                const delivery = moment(item.expectedDelivery);
                const now = moment();

                const isToday = delivery.isSame(now, 'day');
                const isTomorrow = delivery.diff(now, 'days') === 1;
                const isDeliveryUrgent = delivery.diff(now, 'hours') <= 24;

                const dayLabel = isToday
                  ? 'Hoje'
                  : isTomorrow
                  ? 'Amanhã'
                  : delivery.format('DD/MM');

                const time = delivery.format('HH:mm');

                const subLabel = isToday
                  ? 'Chega hoje'
                  : isTomorrow
                  ? 'Chega amanhã'
                  : delivery.fromNow();

                return (
                  <Tooltip title={delivery.format('DD/MM/YYYY HH:mm')}>
                    <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
                      <Typography.Text
                        strong
                        type={isDeliveryUrgent ? 'warning' : undefined}
                      >
                        {dayLabel} {time}
                      </Typography.Text>

                      <div>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {delivery.format('DD/MM')} • {subLabel}
                        </Typography.Text>
                      </div>
                    </div>
                  </Tooltip>
                );
              },
            },
            {
              align: 'right',
              render: (_, item) => (
                <Button
                  shape="circle"
                  onClick={() => {
                    handleAddProduct(item);
                    setPossibilities([]);
                  }}
                  type="primary"
                  icon={<Check size={16} />}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>
    </>
  );
}
